import { useAppConfig } from '#/config/AppConfigContext';
import { useFetchFn } from '#/hooks/useFetchFn';
import type { ApiJob } from '#/modules/listing/jobs/api/types/ApiJob';
import { searchAlgoliaFetchSimilarJobs } from '#/modules/search/algolia/fetch/similarJobs/searchAlgoliaFetchSimilarJobs';
import { useSearchLocationOrEmpty } from '#/modules/search/contexts/SearchContext';
import {
  type SearchSavedListingsStore,
  useSearchSavedListingsSyncAlgoliaHits,
} from '#/modules/search/zustand-stores/searchSavedListingsStore';
import { ViewListingPageSidebarSuggestionsContents } from './ViewListingPageSidebarSuggestionsContents';

type Props = {
  listing: ApiJob;
  searchSavedListingsStore: SearchSavedListingsStore;
};

export function ViewJobPageSidebarSuggestions({
  listing,
  searchSavedListingsStore,
}: Props) {
  const { appConfig } = useAppConfig();
  const userSearchLocation = useSearchLocationOrEmpty();

  const fetchArgs = [
    listing.name,
    'synonym' in listing ? listing.synonym : '',
    listing.functions,
    userSearchLocation,
    listing.org?.areasOfFocusKeys,
    appConfig,
  ] as Parameters<typeof searchAlgoliaFetchSimilarJobs>;

  // We don't want to change the results as the user navigates
  // But the search location might load after this functions is called, so make sure to clear it
  const cacheKey = userSearchLocation
    ? 'with-search-location'
    : 'without-search-location';

  const [similarListingsResponse] = useFetchFn({
    fn: searchAlgoliaFetchSimilarJobs,
    args: fetchArgs,
    cacheKey,
  });

  useSearchSavedListingsSyncAlgoliaHits({
    hits: similarListingsResponse?.hits,
    searchSavedListingsStore,
  });

  if (!similarListingsResponse) {
    return null;
  }

  return (
    <ViewListingPageSidebarSuggestionsContents
      similarListingsResponse={similarListingsResponse}
      listing={listing}
      searchSavedListingsStore={searchSavedListingsStore}
    />
  );
}
